'use client';

import Link from 'next/link';
import { useQuery } from '@apollo/client/react';
import { ArrowLeft, ClipboardList } from 'lucide-react';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { ArticleNameBlock, ProductImage, Spinner } from '@paeffgen/react/crm-documents/ui';
import { formatDate, formatQuantity, formatRequestPackageBreakdown } from '@paeffgen/react/crm-documents/util';
import { MATERIAL_REQUEST_QUERY, type MaterialRequest } from '@paeffgen/react/crm-documents/data-access-graphql';
import { useReorderRequest } from './use-reorder-request';
import { ReorderChoiceDialog } from './reorder-choice-dialog';

function deliveryLabel(r: MaterialRequest): string | null {
  if (r.desiredDeliveryDate) return formatDate(r.desiredDeliveryDate, 'dd.MM.yyyy');
  if (r.desiredDeliveryWeek) {
    const [, w] = r.desiredDeliveryWeek.split('-W');
    return `KW ${w}`;
  }
  return null;
}

export function RequestDetailView({ id }: { id: string }) {
  const { adminQueryVars } = useAdmin();
  const reorder = useReorderRequest();

  const { data, loading, error } = useQuery<{ materialRequest: MaterialRequest | null }>(
    MATERIAL_REQUEST_QUERY,
    { variables: { id, ...adminQueryVars }, fetchPolicy: 'cache-and-network' },
  );

  const request = data?.materialRequest ?? null;

  if (loading && !request) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!loading && error) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <h1 className="text-xl font-bold text-secondary mb-2">Anfrage konnte nicht geladen werden</h1>
        <Link href="/materialanfrage" className="text-primary font-bold hover:underline">
          Zurück zur Anfrage
        </Link>
      </div>
    );
  }

  if (!loading && !error && !request) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <h1 className="text-xl font-bold text-secondary mb-2">Anfrage nicht gefunden</h1>
        <Link href="/materialanfrage" className="text-primary font-bold hover:underline">
          Zurück zur Anfrage
        </Link>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const count = request.items.length;
  const delivery = deliveryLabel(request);

  return (
    <div className="space-y-6">
      <Link href="/materialanfrage" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-secondary transition-colors">
        <ArrowLeft size={16} />
        Zurück zur Anfrage
      </Link>

      {/* Kopf */}
      <div>
        <h1 className="text-2xl font-bold text-secondary tracking-tight">
          {request.commission || 'Anfrage'}
        </h1>
        <p className="text-text-muted mt-1 tabular-nums">
          {formatDate(request.updatedAt, 'dd.MM.yyyy')} · {count} {count === 1 ? 'Position' : 'Positionen'}
          {request.navDocumentNo ? ` · ${request.navDocumentNo}` : ''}
        </p>
      </div>

      {/* Details (read-only) */}
      {(delivery || request.note) && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-[0_2px_4px_rgba(44,44,43,0.05)] space-y-3 text-sm">
          {delivery && (
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">Wunsch-Liefertermin</span>
              <span className="font-semibold text-secondary">{delivery}</span>
            </div>
          )}
          {request.note && (
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">Nachricht an Sachbearbeiter</span>
              <span className="text-secondary whitespace-pre-wrap">{request.note}</span>
            </div>
          )}
        </div>
      )}

      {/* Positionen (read-only) */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)] divide-y divide-zinc-100">
        {request.items.map((item) => {
          const breakdown = formatRequestPackageBreakdown(item);
          return (
            <div key={item.id} className="p-4 flex gap-3 items-start">
              <ProductImage src={item.imageUrl} alt={item.description || item.articleNumber} size="md" className="bg-white border-zinc-200 shrink-0" />
              <div className="flex-1 min-w-0">
                <ArticleNameBlock
                  articleNumber={item.articleNumber}
                  description={item.description || item.articleNumber}
                  description2={item.description2}
                  descriptionClassName="font-semibold text-secondary"
                  description2ClassName="text-sm text-zinc-600"
                  articleNumberClassName="text-xs text-zinc-500 font-mono"
                />
                {item.note && <p className="text-sm text-zinc-600 mt-1 italic">{item.note}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-secondary tabular-nums whitespace-nowrap">
                  {formatQuantity(item.quantity)} <span className="text-zinc-600 font-normal">{item.unit}</span>
                </p>
                {breakdown && <p className="text-xs text-zinc-500 tabular-nums mt-0.5">{breakdown}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Aktion */}
      <button
        type="button"
        onClick={() => reorder.requestReorder(request)}
        className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover shadow-lg shadow-orange-200 transition-all"
      >
        <ClipboardList size={18} />
        Erneut anfragen
      </button>

      <ReorderChoiceDialog reorder={reorder} />
    </div>
  );
}
